import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full border border-slate-200 bg-[#FFFFFF]/60 px-4 py-3.5 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors duration-300 focus:border-[#6D28D9]/60";

export const MobileAuth = ({ onSwitchToEmail }) => {
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [mpin, setMpin] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const done = () => {
    const redirect = sessionStorage.getItem("post_auth_redirect");
    sessionStorage.removeItem("post_auth_redirect");
    navigate(redirect || "/client/dashboard");
  };

  const run = async (fn) => { setLoading(true); try { await fn(); } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); } };

  const sendOtp = () => run(async () => {
    const { data } = await api.post("/auth/mobile/send-otp", { phone });
    setDemoCode(data.demo ? data.demo_code : "");
    if (data.has_mpin) { setStep("mpin"); } else { setStep("otp"); toast.success(data.demo ? "Demo code shown below." : "Code sent to your phone."); }
  });

  const verifyOtp = () => run(async () => {
    const { data } = await api.post("/auth/mobile/verify-otp", { phone, code });
    if (!data.has_mpin) { setStep("setmpin"); toast.success("Verified — set a 4-digit MPIN."); } else { toast.success("Signed in."); done(); }
  });

  const setMpinFn = () => run(async () => { await api.post("/auth/mobile/set-mpin", { mpin }); toast.success("MPIN set. Welcome!"); done(); });
  const loginMpin = () => run(async () => { await api.post("/auth/mobile/login-mpin", { phone, mpin }); toast.success("Welcome back."); done(); });

  const otpToOtp = () => run(async () => { const { data } = await api.post("/auth/mobile/send-otp", { phone }); setDemoCode(data.demo ? data.demo_code : ""); setMpin(""); setStep("otp"); });

  return (
    <div data-testid="mobile-auth" className="mt-8 space-y-4 border border-slate-200 bg-[#F6F4FD]/70 p-8 backdrop-blur">
      {step === "phone" && (
        <>
          <label className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">Mobile number</label>
          <div className="flex items-center gap-2">
            <span className="border border-slate-200 bg-[#FFFFFF]/60 px-3 py-3.5 font-mono2 text-sm text-slate-500">+91</span>
            <input data-testid="mobile-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile number" className={inputCls} />
          </div>
          <button data-testid="mobile-continue-button" disabled={loading || phone.length < 10} onClick={sendOtp} className="flex w-full items-center justify-center gap-2 bg-[#6D28D9] px-8 py-4 font-mono2 text-xs font-semibold uppercase tracking-[0.22em] text-[#FFFFFF] transition-colors hover:bg-[#5B21B6] disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Phone className="h-3.5 w-3.5" /> Continue <ArrowRight className="h-3.5 w-3.5" /></>}
          </button>
        </>
      )}

      {step === "otp" && (
        <>
          <p className="text-sm text-slate-500">Enter the code sent to <span className="text-slate-800">+91 {phone}</span></p>
          {demoCode && <p data-testid="mobile-demo-code" className="border border-[#6D28D9]/30 bg-[#6D28D9]/[0.06] px-3 py-2 font-mono2 text-xs text-[#6D28D9]">Demo code: {demoCode}</p>}
          <input data-testid="mobile-otp-input" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" className={`${inputCls} tracking-[0.4em]`} />
          <button data-testid="mobile-verify-button" disabled={loading || code.length < 4} onClick={verifyOtp} className="flex w-full items-center justify-center gap-2 bg-[#6D28D9] px-8 py-4 font-mono2 text-xs font-semibold uppercase tracking-[0.22em] text-[#FFFFFF] transition-colors hover:bg-[#5B21B6] disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & continue"}
          </button>
          <button type="button" onClick={() => setStep("phone")} className="w-full text-center font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-[#6D28D9]">Change number</button>
        </>
      )}

      {step === "setmpin" && (
        <>
          <div className="flex items-center gap-2 text-[#6D28D9]"><ShieldCheck className="h-4 w-4" /><p className="font-display text-lg">Set a 4-digit MPIN</p></div>
          <p className="text-sm text-slate-500">You'll use this for faster sign-in next time.</p>
          <input data-testid="mobile-set-mpin-input" inputMode="numeric" maxLength={6} value={mpin} onChange={(e) => setMpin(e.target.value.replace(/\D/g, ""))} placeholder="Choose MPIN" className={`${inputCls} tracking-[0.4em]`} />
          <button data-testid="mobile-set-mpin-button" disabled={loading || mpin.length < 4} onClick={setMpinFn} className="w-full bg-[#6D28D9] px-8 py-4 font-mono2 text-xs font-semibold uppercase tracking-[0.22em] text-[#FFFFFF] transition-colors hover:bg-[#5B21B6] disabled:opacity-50">
            {loading ? "Saving…" : "Set MPIN & enter"}
          </button>
        </>
      )}

      {step === "mpin" && (
        <>
          <div className="text-center">
            <p className="font-display text-xl">Welcome back</p>
            <p className="mt-1 font-mono2 text-xs text-slate-500">+91 {phone}</p>
          </div>
          <input data-testid="mobile-login-mpin-input" inputMode="numeric" maxLength={6} value={mpin} onChange={(e) => setMpin(e.target.value.replace(/\D/g, ""))} placeholder="Enter MPIN" className={`${inputCls} text-center tracking-[0.5em]`} />
          <button data-testid="mobile-login-mpin-button" disabled={loading || mpin.length < 4} onClick={loginMpin} className="w-full bg-[#6D28D9] px-8 py-4 font-mono2 text-xs font-semibold uppercase tracking-[0.22em] text-[#FFFFFF] transition-colors hover:bg-[#5B21B6] disabled:opacity-50">
            {loading ? "Checking…" : "Submit"}
          </button>
          <button type="button" data-testid="mobile-use-otp" onClick={otpToOtp} className="w-full text-center font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-[#6D28D9]">Login with OTP instead</button>
        </>
      )}

      <button type="button" data-testid="mobile-switch-email" onClick={onSwitchToEmail} className="w-full pt-2 text-center font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-[#6D28D9]">Use email instead</button>
    </div>
  );
};
